import React, { useEffect, useState } from "react";
import "@/App.css";
import "./yasharal-live.css";
import "./lib/http";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

import Header from "./components/Header";
import AbarytDateBar from "./components/AbarytDateBar";
import Footer from "./components/Footer";
import LeadCaptureModal, { nextLeadCaptureEligibleAt } from "./components/LeadCaptureModal";
import CartDrawer from "./components/CartDrawer";
import EmailCheckModal from "./components/EmailCheckModal";
import AutoLogout from "./components/AutoLogout";
import CommunicationsPortalLink from "./components/CommunicationsPortalLink";
import StudioQuickLauncher from "./components/StudioQuickLauncher";
import DiagnosticRecovery from "./components/DiagnosticRecovery";
import { CartProvider } from "./context/CartContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AbarytDateProvider } from "./context/AbarytDateContext";
import MinistryWorkspace from "./pages/MinistryWorkspace";

import Home from "./pages/Home";
import About from "./pages/About";
import Governance from "./pages/Governance";
import Declaration from "./pages/Declaration";
import Constitution from "./pages/Constitution";
import OfficialDocuments from "./pages/OfficialDocuments";
import BecomeNational from "./pages/BecomeNational";
import Programs from "./pages/Programs";
import FightFiTash from "./pages/FightFiTash";
import MediaHub from "./pages/MediaHub";
import NewsBlog from "./pages/NewsBlog";
import Shop from "./pages/Shop";
import Donate from "./pages/Donate";
import Contact from "./pages/Contact";
import Shaba from "./pages/Shaba";
import BrandFightFiTash from "./pages/BrandFightFiTash";
import BrandShaba from "./pages/BrandShaba";
import MembersHub from "./pages/MembersHub";
import MembersNational from "./pages/MembersNational";
import MembersSubscriber from "./pages/MembersSubscriber";
import AdminSubmissions from "./pages/AdminSubmissions";
import NationalsAdmin from "./pages/NationalsAdmin";
import SubscribersSignup from "./pages/SubscribersSignup";
import AdminPayments from "./pages/AdminPayments";
import AdminAccountDirectory from "./pages/AdminAccountDirectory";
import MusicOrganizationPortal from "./pages/MusicOrganizationPortal";
import MusicAdminSuite from "./pages/MusicAdminSuite";
import AccountSettings from "./pages/AccountSettings";
import MusicCollaborations from "./components/MusicCollaborations";
import MusicProfessionalDirectory from "./pages/MusicProfessionalDirectory";
import MusicOrganizations from "./pages/MusicOrganizations";
import UnifiedPortal from "./pages/UnifiedPortal";
import CommunicationsHome from "./pages/CommunicationsHome";
import NationCommunications from "./pages/NationCommunications";
import NationStudio from "./pages/NationStudio";
import PublicLive from "./pages/PublicLive";
import CommunicationsRecordings from "./pages/CommunicationsRecordings";
import YasharalLiveSignup from "./pages/YasharalLiveSignup";
import YasharalLivePresenterInvite from "./pages/YasharalLivePresenterInvite";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import CartPage from "./pages/CartPage";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AdminDiagnostics from "./pages/AdminDiagnostics";
import DailyDevotional from "./pages/DailyDevotional";
import DevotionalPlanner from "./pages/DevotionalPlanner";
import AccountLogin from "./pages/AccountLogin";
import AccountSignup from "./pages/AccountSignup";
import TurahCommands from "./pages/TurahCommands";
import MyLibrary from "./pages/MyLibrary";
import YaHuaHsGirls from "./pages/YaHuaHsGirls";
import YaHuaHsGirlsActivities from "./pages/YaHuaHsGirlsActivities";
import AncientPathYouth from "./pages/AncientPathYouth";
import AlapBayt from "./pages/AlapBayt";
import FreeResources from "./pages/FreeResources";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

function ApiStatus() {
  const [status, setStatus] = useState("checking");
  useEffect(() => {
    const checkHealth = async () => {
      try {
        await axios.get(`${API}/health`);
        setStatus("ok");
      } catch {
        setStatus("error");
      }
    };
    checkHealth();
    const interval = setInterval(checkHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="fixed bottom-4 right-4 z-40" data-testid="api-status">
      <div
        className={`w-3 h-3 rounded-full ${status === "ok" ? "bg-green-500" : status === "error" ? "bg-red-500" : "bg-yellow-500"}`}
        title={`API Status: ${status}`}
      />
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function DiagnosticNavigationTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    import("./lib/diagnostics").then(({ recordNavigation }) => recordNavigation(pathname));
  }, [pathname]);
  return null;
}

function LeadModalController() {
  const { user, loading: authLoading } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [revision, setRevision] = useState(0);
  const location = useLocation();
  const SUPPRESSED_PREFIXES = [
    "/donate", "/shop", "/cart", "/checkout", "/brands", "/portal",
    "/nationals", "/communications", "/yasharal-live", "/subscribers", "/admin", "/members",
    "/daily-devotional", "/turah-commands", "/my-library", "/login", "/signup",
  ];
  const isSuppressedRoute = SUPPRESSED_PREFIXES.some(path => location.pathname.startsWith(path));

  useEffect(() => {
    setShowModal(false);
    if (isSuppressedRoute || authLoading || user) return;
    let timer;
    let active = true;
    const schedule = () => {
      if (!active || authLoading || user) return;
      const eligibleAt = nextLeadCaptureEligibleAt();
      if (eligibleAt === null) return;
      // Always allow the original five seconds before the first appearance.
      const waitMs = Math.max(5000, eligibleAt - Date.now());
      timer = setTimeout(() => {
        if (!active || authLoading || user) return;
        const reevaluated = nextLeadCaptureEligibleAt();
        if (reevaluated === null) return;
        if (reevaluated > Date.now()) { schedule(); return; }
        setShowModal(true);
      }, waitMs);
    };
    schedule();
    return () => { active = false; clearTimeout(timer); };
  }, [isSuppressedRoute, location.pathname, revision, authLoading, user]);

  const dismiss = () => { setShowModal(false); setRevision(n => n + 1); };
  return (
    <LeadCaptureModal
      isOpen={showModal && !authLoading && !user && !isSuppressedRoute}
      onClose={dismiss}
      onComplete={() => {
        setShowModal(false);
        setRevision(n => n + 1);
        toast.success("Thank you. We will be in touch soon.");
      }}
    />
  );
}

function PortalWithCommunications() {
  // The compact, filterable Live feed is now inside the account dashboard.
  return <UnifiedPortal />;
}


function GlobalLiveBanner() {
  const [live, setLive] = useState(null);
  const [, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const response = await axios.get(`${API}/communications/public-live`, {
          headers: { "Cache-Control": "no-cache" },
          params: { _ts: Date.now() },
        });
        if (alive) setLive(response.data || null);
      } catch {
        if (alive) setLive(null);
      }
    };
    load();
    const timer = setInterval(load, 5000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (!live?.is_live) return undefined;
    const timer = setInterval(() => setTick((value) => value + 1), 1000);
    return () => clearInterval(timer);
  }, [live?.is_live]);

  if (!live?.is_live) return null;

  let elapsed = null;
  if (live.started_at) {
    const started = new Date(live.started_at).getTime();
    if (Number.isFinite(started)) {
      const total = Math.max(0, Math.floor((Date.now() - started) / 1000));
      const hours = Math.floor(total / 3600);
      const minutes = Math.floor((total % 3600) / 60);
      const seconds = total % 60;
      elapsed = hours > 0
        ? `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
        : `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    }
  }

  return (
    <Link
      to="/live"
      data-testid="global-live-now-banner"
      className="relative z-[300] block w-full bg-red-600 text-white hover:bg-red-700 transition-colors"
      aria-label="Watch the live broadcast"
    >
      <div className="mx-auto flex min-h-[40px] max-w-7xl flex-wrap items-center justify-center gap-2 px-4 py-2 text-center text-sm font-bold uppercase tracking-[0.13em]">
        <span className="inline-block h-2.5 w-2.5 rounded-full bg-white animate-pulse" />
        <span>Live now</span>
        {live.title && <span className="hidden sm:inline normal-case tracking-normal font-semibold">• {live.title}</span>}
        {elapsed && <span className="font-mono tracking-normal tabular-nums">• {elapsed}</span>}
        <span className="normal-case tracking-normal underline underline-offset-2">Watch live →</span>
      </div>
    </Link>
  );
}

function AppContent() {
  const location = useLocation();
  const communicationsApp = location.pathname.startsWith("/communications") || location.pathname.startsWith("/yasharal-live");

  return (
    <div className="App min-h-screen flex flex-col">
      <AutoLogout />
      <ScrollToTop />
      <DiagnosticNavigationTracker />
      <DiagnosticRecovery />
      {communicationsApp && <AbarytDateBar />}
      {!communicationsApp && <Header />}
      {!location.pathname.includes("/studio/") && <GlobalLiveBanner />}

      <main className="flex-grow">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/governance" element={<Governance />} />
          <Route path="/governance/declaration" element={<Declaration />} />
          <Route path="/governance/constitution" element={<Constitution />} />
          <Route path="/governance/documents" element={<OfficialDocuments />} />
          <Route path="/become-national" element={<BecomeNational />} />
          <Route path="/programs" element={<Programs />} />
          <Route path="/fightfitash" element={<FightFiTash />} />
          <Route path="/media" element={<MediaHub />} />
          <Route path="/news" element={<NewsBlog />} />
          <Route path="/live" element={<PublicLive />} />
          <Route path="/daily-devotional" element={<DailyDevotional />} />
          <Route path="/daily-devotional/:dayOfYear" element={<DailyDevotional />} />
          <Route path="/turah-commands" element={<TurahCommands />} />
          <Route path="/my-library" element={<MyLibrary />} />
          <Route path="/login" element={<AccountLogin />} />
          <Route path="/signup" element={<AccountSignup />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/donate" element={<Donate />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/shaba" element={<Shaba />} />
          <Route path="/YaHsgirls" element={<YaHuaHsGirls />} />
          <Route path="/YaHsgirls/activities" element={<YaHuaHsGirlsActivities />} />
          <Route path="/free-resources" element={<FreeResources />} />
          <Route path="/free-resources/alap-bayt" element={<AlapBayt />} />
          <Route path="/ancient-path-youth" element={<AncientPathYouth />} />
          <Route path="/brands/fightfitash" element={<BrandFightFiTash />} />
          <Route path="/brands/shaba" element={<BrandShaba />} />
          <Route path="/members" element={<MembersHub />} />
          <Route path="/members/national" element={<MembersNational />} />
          <Route path="/members/subscriber" element={<MembersSubscriber />} />
          <Route path="/members/subscriber/monthly" element={<MembersSubscriber />} />
          <Route path="/nationals/login" element={<AccountLogin />} />
          <Route path="/nationals/portal" element={<PortalWithCommunications />} />
          <Route path="/nationals/admin" element={<NationalsAdmin />} />
          <Route path="/nationals/devotional-planner" element={<DevotionalPlanner />} />

          {/* Public Yasharal Live product routes. These do not confer Nation or subscriber access. */}
          <Route path="/yasharal-live/login" element={<AccountLogin />} />
          <Route path="/yasharal-live/signup" element={<YasharalLiveSignup />} />
          <Route path="/yasharal-live/presenter/:roomId" element={<YasharalLivePresenterInvite />} />
          <Route path="/yasharal-live" element={<CommunicationsHome />} />
          <Route path="/yasharal-live/rooms/:roomId" element={<NationCommunications />} />
          <Route path="/yasharal-live/guest/:roomId" element={<NationCommunications />} />
          <Route path="/yasharal-live/studio/:roomId" element={<NationStudio />} />
          <Route path="/yasharal-live/recordings" element={<CommunicationsRecordings />} />

          {/* Legacy Communications URLs remain valid while existing invites/bookmarks transition. */}
          <Route path="/communications" element={<CommunicationsHome />} />
          <Route path="/communications/rooms/:roomId" element={<NationCommunications />} />
          <Route path="/communications/guest/:roomId" element={<NationCommunications />} />
          <Route path="/communications/studio/:roomId" element={<NationStudio />} />
          <Route path="/communications/recordings" element={<CommunicationsRecordings />} />

          <Route path="/subscribers/login" element={<AccountLogin />} />
          <Route path="/subscribers/signup" element={<SubscribersSignup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/admin/payments" element={<AdminPayments />} />
          <Route path="/admin/accounts" element={<AdminAccountDirectory />} />
          <Route path="/music" element={<MusicOrganizations />} />
          <Route path="/music/admin" element={<MusicAdminSuite/>}/>
          <Route path="/music/records" element={<MusicOrganizationPortal area="records" />} />
          <Route path="/music/publishing" element={<MusicOrganizationPortal area="publishing" />} />
          <Route path="/music/directory" element={<MusicProfessionalDirectory/>} />
          <Route path="/music/collaborations" element={<main className="mx-auto max-w-5xl space-y-4 px-4 py-8"><Link className="text-blue-800 underline" to="/portal">Back to My Account</Link><MusicCollaborations/></main>} />
          <Route path="/admin/submissions" element={<AdminSubmissions />} />
          <Route path="/admin/diagnostics" element={<AdminDiagnostics />} />
          <Route path="/portal" element={<PortalWithCommunications />} />
          <Route path="/portal/tools" element={<PortalWithCommunications />} />
          <Route path="/account/settings" element={<AccountSettings/>} />
          <Route path="/ministries/:ministryKey" element={<MinistryWorkspace />} />
        </Routes>
      </main>

      <StudioQuickLauncher />
      {!communicationsApp && <Footer />}
      {!communicationsApp && <CartDrawer />}
      {!communicationsApp && <EmailCheckModal />}
      <LeadModalController />
      {!communicationsApp && <ApiStatus />}
      <Toaster position="top-right" richColors />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AbarytDateProvider>
          <CartProvider>
            <AppContent />
          </CartProvider>
        </AbarytDateProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
